import React, { useState } from 'react';
import { useUserPermissions } from '../context/UserPermissionsContext';
import Card from '@albertsons/uds/molecule/Card';
import Button from '@albertsons/uds/molecule/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import getMandatoryFieldsTable from '../services/selfServiceServices/getMandatoryFieldsTable';
import getFieldTypeForTable from '../services/selfServiceServices/getFieldTypeForTable';
import getDoorStatuses from '../services/selfServiceServices/getDoorStatuses';
import getTableHeaders from '../services/selfServiceServices/getTableHeaders';
import { submitTableData } from '../services/selfServiceServices/submitTableData';
import EditableTable from './EditableTable';
import UploadFileCardSelfService from './uploadFileCardSelfService';
import FillTableCard from './FillTableCard';
import BananaSequencing from './BananaSequencing';

interface SelfServiceDashboardProps {
    selectedDC?: string;
    selectedOrg?: string;
    tabType?: string;
    activeTab?: number;
    onUpload?: (files: File[]) => void;
    onUploadFilTable?: (filesFillTable: File[]) => void;
    selectedGroup?: string;
    selectedSubGroup?: string;
}

const SelfServiceDashboard: React.FC<SelfServiceDashboardProps> = ({ 
    selectedOrg,
    selectedGroup = '',
    selectedSubGroup = ''
}) => {
    const { userPermissions } = useUserPermissions();
    const isBananaSequencingAuthorized = userPermissions?.roles.some(role =>
        (role.capabilities || []).some(cap => cap.toLowerCase().startsWith('banana'))
    ) ?? false;

    const fileInputRef = React.useRef<HTMLInputElement>(null);
    
    const [uploadCardOpen, setUploadCardOpen] = useState(false);
    const [fillTableCardOpen, setFillTableCardOpen] = useState(true);
    
    // File upload states
    const [files, setFiles] = useState<File[]>([]);
    
    // Table data states
    const [tableHeaders, setTableHeaders] = useState<string[]>([]);
    const [tableData, setTableData] = useState<string[][]>([]);
    const [showTable, setShowTable] = useState<boolean>(true);
    const [showMainView, setShowMainView] = useState<boolean>(true);
    
    // Validation states
    const [mandatoryFields, setMandatoryFields] = useState<string[]>([]);
    const [fieldTypes, setFieldTypes] = useState<{ [fieldName: string]: 'string' | 'number' | 'decimal' | 'date' | 'boolean' }>({});

    // Selected rows state
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

    // Validation error state
    const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false);

    // Snackbar states
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');

    const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const handleValidationChange = (hasErrors: boolean) => {
        setHasValidationErrors(hasErrors);
    };

    // Fetch headers when group and subgroup are selected
    React.useEffect(() => {
        const fetchHeaders = async () => {
            if (selectedOrg && selectedGroup && selectedSubGroup) {
                try {
                    const env = process.env.REACT_APP_ENV || '';
                  
                    const [headers, fetchedMandatoryFields, fetchedFieldTypes] = await Promise.all([
                        getTableHeaders(selectedOrg, env, selectedGroup, selectedSubGroup),
                        getMandatoryFieldsTable(selectedOrg, env, selectedGroup, selectedSubGroup),
                        getFieldTypeForTable(selectedOrg, env, selectedGroup, selectedSubGroup)
                    ]);
                    
                    setTableHeaders(headers);
                    setMandatoryFields(fetchedMandatoryFields);
                    setFieldTypes(fetchedFieldTypes);
                } catch (error) {
                    console.error('❌ Error fetching headers:', error);
                }
            }
        };

        fetchHeaders();
    }, [selectedOrg, selectedGroup, selectedSubGroup]);

    // selfServiceDashboard.tsx
    React.useEffect(() => {
    // Clear the table and related states when the org, group, or subgroup changes
    setTableData([]);
    setSelectedRows(new Set());
    setFiles([]);
    setTableHeaders([]);
    setMandatoryFields([]);
    setFieldTypes({});
    // Don't hide the table in self service - users should be able to manually enter data
    // setShowTable(false); // Commented out to keep Fill Table visible
    setShowMainView(true);
    // Reset card states to default
    setUploadCardOpen(false);
    setFillTableCardOpen(true);
    // Clear snackbar messages
    setSnackbarOpen(false);
    setSnackbarMessage('');
    // Clear file input reference
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }
    
    }, [selectedOrg, selectedGroup, selectedSubGroup]);


    const handleFileSelect = async (selectedFiles: File[]) => {
        if (selectedFiles.length > 0) {
            const file = selectedFiles[0];
            try {
                // Validate prerequisites before processing
                if (!selectedOrg || !selectedGroup || !selectedSubGroup) {
                    setSnackbarMessage('Please select Organization, Group, and Sub Group first');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    setFiles([]); // Clear the file
                    return;
                }                
                let { data } = await parseCSV(file);                
                const env = process.env.REACT_APP_ENV || '';                
                let headers: string[] = await getTableHeaders(selectedOrg, env, selectedGroup, selectedSubGroup);
                
                if (!headers || headers.length === 0) {
                    setSnackbarMessage('Failed to fetch table headers from API. Please try again.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }
                
                // Special handling for Shipping - Door Clean Up: automatically add Status column if missing
                if (selectedGroup === 'Shipping' && selectedSubGroup === 'Door Clean Up') {
                   
                    const hasStatusColumn = headers.some(header => 
                        header.trim().toLowerCase() === 'status'
                    );
                    
                    let statusColumnIndex = -1;
                    
                    if (!hasStatusColumn) {
                        // Add "Status" column to headers
                        headers = [...headers, 'Status'];
                        statusColumnIndex = headers.length - 1;
                        
                        // Add empty Status value to each data row
                        data = data.map(row => [...row, '']);
                   } else {
                        // Find the status column index
                        statusColumnIndex = headers.findIndex(header => 
                            header.trim().toLowerCase() === 'status'
                        );
                        }
                    
                    const doorIdColumnIndex = headers.findIndex(header => {
                        const normalized = header.trim().toLowerCase().replace(/[\s_-]/g, '');
                        const match = normalized === 'doorid' || 
                                     normalized === 'dockdoorid' || 
                                     normalized.includes('doorid') ||
                                     normalized.includes('dockdoor');
                        
                        return match;
                    });
                    
                    if (doorIdColumnIndex !== -1 && selectedOrg) {
                            const doorIds = data
                            .map(row => row[doorIdColumnIndex])
                            .filter(id => id && id.trim() !== '');

                        if (doorIds.length > 0) {
                            const doorStatusMap = await getDoorStatuses(selectedOrg, doorIds);
                                data = data.map((row, index) => {
                                const doorId = row[doorIdColumnIndex];
                                const status = doorStatusMap.get(doorId) || '';
                                const updatedRow = [...row];
                                updatedRow[statusColumnIndex] = status;
                                if (index < 3) {                                   
                                }
                                return updatedRow;
                            });
                            
                        } else {
                            console.warn('⚠️ No valid door IDs found in the file');
                        }
                    } else if (doorIdColumnIndex === -1) {
                        console.error('❌ Door ID column not found in the uploaded file');
                       } else if (!selectedOrg) {
                        console.error('❌ Organization not selected');
                    }
                }
                
                setTableHeaders(headers);
                setTableData(data);
                setShowTable(true); // Show the Fill Table panel
                
                // Fetch mandatory fields and field types from API
                if (selectedOrg && selectedGroup && selectedSubGroup) {
                    // Call APIs in parallel
                    const [fetchedMandatoryFields, fetchedFieldTypes] = await Promise.all([
                        getMandatoryFieldsTable(selectedOrg, env, selectedGroup, selectedSubGroup),
                        getFieldTypeForTable(selectedOrg, env, selectedGroup, selectedSubGroup)
                    ]);
                    
                    setMandatoryFields(fetchedMandatoryFields);
                    setFieldTypes(fetchedFieldTypes);
                }
                
                setSnackbarMessage('File loaded successfully');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                
            } catch (error) {
                setSnackbarMessage(`Error parsing CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        }
    };

    // Handle clear button click
    const handleClearClick = () => {
        setFiles([]);
        setTableData([]);
        setSelectedRows(new Set());
        // Reset file input to allow re-uploading the same file
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setSnackbarMessage('Files cleared');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    // Handle clear table data
    const handleClearTable = () => {
        // Create 25 empty rows instead of clearing everything
        if (tableHeaders.length > 0) {
            const emptyRows = Array.from({ length: 25 }, () => 
                tableHeaders.map(() => '')
            );
            setTableData(emptyRows);
        } else {
            setTableData([]);
        }
        
        // Clear selected rows
        setSelectedRows(new Set());
        setFiles([]);
        setSnackbarMessage('Table cleared');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
    };

    // Parse CSV file - now treats all rows as data (no header row in CSV)
    const parseCSV = (file: File): Promise<{ data: string[][] }> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    const lines = text.split('\n').filter(line => line.trim() !== '');
                    
                    if (lines.length === 0) {
                        reject(new Error('CSV file is empty'));
                        return;
                    }

                    // Parse CSV line with proper quote handling
                    const parseCSVLine = (line: string): string[] => {
                        const result: string[] = [];
                        let current = '';
                        let inQuotes = false;
                        
                        for (let i = 0; i < line.length; i++) {
                            const char = line[i];
                            const nextChar = line[i + 1];
                            
                            if (char === '"' && inQuotes && nextChar === '"') {
                                current += '"';
                                i++;
                            } else if (char === '"') {
                                inQuotes = !inQuotes;
                            } else if (char === ',' && !inQuotes) {
                                result.push(current.trim());
                                current = '';
                            } else {
                                current += char;
                            }
                        }
                        result.push(current.trim());
                        return result;
                    };

                    // Skip first line (CSV header) and parse only data rows starting from line 2
                    const data = lines.slice(1).map(line => parseCSVLine(line));

                    if (data.length === 0) {
                        reject(new Error('No valid data rows found'));
                        return;
                    }

                    resolve({ data });
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    // Handle back button from table view
    const handleBackToMain = () => {
        setShowMainView(true);
        setShowTable(false);
        setTableHeaders([]);
        setTableData([]);
        setFiles([]);
    };

    // Debounce timer ref for cell edit
    const cellEditTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const statusClearTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const statusClearedRef = React.useRef<{ [key: number]: boolean }>({});
    const preserveStatusRef = React.useRef<{ [key: number]: string }>({});
    const doorStatusReqIdRef = React.useRef<{ [rowIndex: number]: number }>({});
    const doorStatusAbortRef = React.useRef<{ [rowIndex: number]: AbortController | null }>({});


    const handleCellEdit = async (rowIndex: number, columnName: string, value: string) => {
       if (selectedGroup === 'Shipping' && selectedSubGroup === 'Door Clean Up') {
            const normalized = columnName.trim().toLowerCase().replace(/[\s_-]/g, '');
            const isDockDoorId = normalized.includes('dockdoor') || normalized.includes('doorid');
           if (isDockDoorId) {
                // Clear any existing timers
                if (cellEditTimerRef.current) {
                    clearTimeout(cellEditTimerRef.current);
                }
                if (statusClearTimerRef.current) {
                    clearTimeout(statusClearTimerRef.current);
                }
                
                // Find Status column index
                const statusColIndex = tableHeaders.findIndex( 
                    (header: string) => header.trim().toLowerCase() === 'status'
                );
                
                
                if (value.trim().length > 0) {
                    // 🔹 Cancel previous in-flight request for this row (if any)
                    if (doorStatusAbortRef.current[rowIndex]) {
                        doorStatusAbortRef.current[rowIndex]!.abort();
                        doorStatusAbortRef.current[rowIndex] = null;
                    }

                    // 🔹 Bump request id for this row; used to ignore stale responses
                    doorStatusReqIdRef.current[rowIndex] =
                        (doorStatusReqIdRef.current[rowIndex] ?? 0) + 1;
                }

                
                if (!value || value.trim().length === 0) {
                 
                    if (statusColIndex !== -1) {
                        // Only clear Status column - don't touch DockDoorId as EditableTable manages it
                        setTableData((prevData: string[][]) => {
                          
                            if (!prevData[rowIndex]) {
                              return prevData;
                            }
                            
                            const actualStatus = prevData[rowIndex][statusColIndex] || '';
                           if (actualStatus !== '') {
                                const newData = [...prevData];
                                newData[rowIndex] = [...newData[rowIndex]];
                                newData[rowIndex][statusColIndex] = '';
                                statusClearedRef.current[rowIndex] = true;
                                // Mark that we want to preserve empty status for this row
                                preserveStatusRef.current[rowIndex] = '';
                                return newData;
                            } else {
                               statusClearedRef.current[rowIndex] = false;
                                return prevData; // No change needed
                            }
                        });
                    }
                    return;
                }

                const actualStatus = tableData[rowIndex] ? (tableData[rowIndex][statusColIndex] || '') : '';
                if (actualStatus === '') {
                    statusClearedRef.current[rowIndex] = false;
                }
                
                // Fetch status for any non-empty DockDoorId value
                if (value.trim().length > 0 && selectedOrg) {
                    // Set a new timer to debounce API calls (wait 500ms after user stops typing)
                    
                cellEditTimerRef.current = setTimeout(async () => {
                // Create a fresh AbortController for this request (if your API supports cancellation)
                const ctrl = new AbortController();
                doorStatusAbortRef.current[rowIndex] = ctrl;

                // Capture the request id at the time we scheduled this call
                const requestId = doorStatusReqIdRef.current[rowIndex] ?? 0;

                try {
                    // 🔸 IMPORTANT: if getDoorStatuses supports AbortSignal, pass ctrl.signal:
                    // getDoorStatuses(org: string, ids: string[], signal?: AbortSignal)
                    const doorStatusMap = await getDoorStatuses(selectedOrg, [value]);

                    // Ignore if a newer request started while we awaited (stale response)
                    if ((doorStatusReqIdRef.current[rowIndex] ?? 0) !== requestId) return;

                    doorStatusAbortRef.current[rowIndex] = null;

                    const status = doorStatusMap.get(value) ?? '';

                    // Replace "Loading..." with actual status (or empty if invalid)
                    if (statusColIndex !== -1) {
                    setTableData((prevData: string[][]) => {
                        const newData = [...prevData];
                        if (newData[rowIndex]) {
                        newData[rowIndex] = [...newData[rowIndex]];
                        newData[rowIndex][statusColIndex] = status; // '' = invalid
                        }
                        return newData;
                    });
                    }

                    // ✅ Show "Not a valid Dock Door Id" ONLY AFTER response AND empty status
                    if (status === '') {
                        setSnackbarMessage(`'${value}' is not a valid Dock Door Id`);
                        setSnackbarSeverity('warning');
                        setSnackbarOpen(true);
                    }
                } catch (err: any) {
                    // Ignore errors from aborted requests
                    if (err?.name === 'AbortError') return;

                    // Also ignore if a newer request started meanwhile
                    if ((doorStatusReqIdRef.current[rowIndex] ?? 0) !== requestId) return;

                    doorStatusAbortRef.current[rowIndex] = null;
                   
                    // Optional: clear Status cell on network error
                    if (statusColIndex !== -1) {
                    setTableData((prevData: string[][]) => {
                        const newData = [...prevData];
                        if (newData[rowIndex]) {
                        newData[rowIndex] = [...newData[rowIndex]];
                        newData[rowIndex][statusColIndex] = '';
                        }
                        return newData;
                    });
                    }

                    // Optional: surface a network error here
                    // setSnackbarMessage('Failed to fetch door status');
                    // setSnackbarSeverity('error');
                    // setSnackbarOpen(true);
                }
                }, 500);
 // Wait 500ms after user stops typing
                }
            }
        }
    };

    // Wrapper for onDataChange that preserves Status when it was just cleared
    const handleDataChange = React.useCallback((newData: string[][]) => {
      // Find Status column index
      const statusColIndex = tableHeaders.findIndex(
          (header: string) => header.trim().toLowerCase() === 'status'
      );
        
        if (statusColIndex !== -1) {
            // Check if any rows have a preserve flag set
            const hasPreserveFlags = Object.keys(preserveStatusRef.current).length > 0;
            
            if (hasPreserveFlags) {
                const preservedData = newData.map((row, rowIndex) => {
                    if (preserveStatusRef.current.hasOwnProperty(rowIndex)) {
                        const preservedStatus = preserveStatusRef.current[rowIndex];
                       const newRow = [...row];
                        newRow[statusColIndex] = preservedStatus;
                        delete preserveStatusRef.current[rowIndex];
                        return newRow;
                    }
                    return row;
                });
                setTableData(preservedData);
                return;
            }
        }
        
       setTableData(newData);
    }, [tableHeaders]);

    const handleSubmit = async () => {
        // Check if any rows are selected
        if (selectedRows.size === 0) {
            setSnackbarMessage('Please select at least one row to submit');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        const validateFunc = (window as any)._validateTableData;
        
        if (validateFunc && typeof validateFunc === 'function') {
            const isValid = validateFunc();
            
            if (!isValid) {
                return;
            }
        }
        
        // Format table data into the required JSON structure - ONLY SELECTED ROWS
        const selectedRowIndices = Array.from(selectedRows).sort((a, b) => a - b);
        
        // Check if this is shipment reset context
        const isShipmentReset = selectedSubGroup?.toLowerCase().includes('shipment reset');
        
        let formattedData;
        
        if (isShipmentReset) {
            // For shipment reset: use specific format with ShipmentId, StopStatus, and ResetStatus
            const shipmentIdIndex = tableHeaders.findIndex(header => 
                header?.toLowerCase().replace(/[\s_-]/g, '') === 'shipmentid'
            );
            const stopStatusIndex = tableHeaders.findIndex(header => 
                header?.toLowerCase().replace(/[\s_-]/g, '') === 'stopstatus'
            );
            const resetStatusIndex = tableHeaders.findIndex(header => 
                header?.toLowerCase().replace(/[\s_-]/g, '') === 'resetstatus'
            );
            
            formattedData = selectedRowIndices
                .map((rowIndex: number) => {
                    const row = tableData[rowIndex];
                    if (!row) return null;
                    
                    // Check if row has any data
                    const hasData = row.some((cell: string) => cell && cell.trim() !== '');
                    if (!hasData) return null;
                    
                    // Create the specific format for shipment reset
                    const rowObject: { [key: string]: string } = {};
                    
                    // Map ShipmentId
                    if (shipmentIdIndex !== -1) {
                        rowObject['ShipmentId'] = row[shipmentIdIndex] || '';
                    }
                    
                    // Map StopStatus
                    if (stopStatusIndex !== -1) {
                        rowObject['StopStatus'] = row[stopStatusIndex] || '';
                    }
                    
                    // Map ResetStatus
                    if (resetStatusIndex !== -1) {
                        rowObject['ResetStatus'] = row[resetStatusIndex] || 'OPEN';
                    }
                    
                    return rowObject;
                })
                .filter((row): row is { [key: string]: string } => row !== null);
        } else {
            // For door planning and other contexts: use the existing format
            formattedData = selectedRowIndices
                .map((rowIndex: number) => {
                    const row = tableData[rowIndex];
                    if (!row) return null;
                    
                    // Check if row has any data
                    const hasData = row.some((cell: string) => cell && cell.trim() !== '');
                    if (!hasData) return null;
                    
                    const rowObject: { [key: string]: string } = {};
                    tableHeaders.forEach((header: string, index: number) => {
                        rowObject[header] = row[index] || '';
                    });
                    return rowObject;
                })
                .filter((row): row is { [key: string]: string } => row !== null); // Filter out null rows
        }
        
        
        // Validate required fields before submission
        if (!selectedOrg || !selectedGroup || !selectedSubGroup) {
            setSnackbarMessage('Please ensure Organization, Group, and Sub Group are selected.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        try {
            await submitTableData({
                org: selectedOrg,
                env: process.env.REACT_APP_ENV || '',
                group: selectedGroup,
                subGroup: selectedSubGroup,
                data: formattedData
            }); setSnackbarMessage(`${selectedGroup} Data Uploaded successfully - No Errors found`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            
            // Clear selected rows immediately
            setSelectedRows(new Set());
            
            // Clear table data after successful submission - create 25 empty rows
            setTimeout(() => {
                const emptyRows = Array.from({ length: 25 }, () => 
                    tableHeaders.map(() => '')
                );
                setTableData(emptyRows);
                setFiles([]);
            }, 2000);
        } catch (error: any) {
            console.error('❌ Error submitting data:', error);
            if (error.response) {
                setSnackbarMessage(`${selectedGroup} Data Uploaded failed: ${error.response.data}`);
            } else if (error.request) {
                setSnackbarMessage(`${selectedGroup} Data Uploaded failed: No response received`);
            } else {
                setSnackbarMessage(`${selectedGroup} Data Uploaded failed: ${error.message || 'Unknown error'}`);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };
    
    if (isBananaSequencingAuthorized && selectedGroup === 'Location' && selectedSubGroup === 'Banana Sequencing') {
        return <BananaSequencing selectedOrg={selectedOrg} />;
    }

    if (!showMainView && showTable) {
        return (
            <div className="popup-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    width: '95vw',
                    height: '90vh',
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <Card 
                        title="Uploaded Data - Editable Table" 
                        isOpen={true} 
                        className="popup-card"
                    >
                        <Card.Header>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                paddingLeft: '10px',
                                paddingRight: '10px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                        Group: {selectedGroup}
                                    </span>
                                    <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                        Sub Group: {selectedSubGroup}
                                    </span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Content>
                            <EditableTable
                                headers={tableHeaders}
                                data={tableData}
                                onDataChange={handleDataChange}
                                maxHeight="calc(90vh - 150px)"
                                showEditControls={true}
                                mandatoryFields={mandatoryFields}
                                fieldTypes={fieldTypes}
                                onValidate={() => true}
                                onValidationChange={handleValidationChange}
                                onCellEdit={handleCellEdit}
                                selectedGroup={selectedGroup}
                                selectedSubGroup={selectedSubGroup}
                                selectedOrg={selectedOrg}
                                actionButtons={
                                    <>
                                        <Button onClick={handleBackToMain}>
                                            Close
                                        </Button>
                                        <Button 
                                            onClick={handleSubmit} 
                                            disabled={hasValidationErrors}
                                        >
                                            Submit
                                        </Button>
                                    </>
                                }
                            />
                        </Card.Content>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="self-service-dashboard">
            <UploadFileCardSelfService
                isOpen={uploadCardOpen}
                setOpen={setUploadCardOpen}
                files={files}
                setFiles={setFiles}
                onFileSelect={handleFileSelect}
                onClearClick={handleClearClick}
                isOrgSelected={!!selectedOrg}
                fileInputRef={fileInputRef}
            />

            <FillTableCard
                isOpen={fillTableCardOpen}
                setOpen={setFillTableCardOpen}
                headers={tableHeaders}
                data={tableData}
                onDataChange={handleDataChange}
                onCellEdit={handleCellEdit}
                mandatoryFields={mandatoryFields}
                fieldTypes={fieldTypes}
                selectedGroup={selectedGroup}
                selectedSubGroup={selectedSubGroup}
                selectedOrg={selectedOrg}
                onSubmit={handleSubmit}
                onClear={handleClearTable}
                showTable={showTable}
                onSelectedRowsChange={setSelectedRows}
                selectedRows={selectedRows}
                onValidationChange={handleValidationChange}
            />
            
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default SelfServiceDashboard;